import React, { useState, useEffect } from 'react';
import portalDocumentService from '../../services/portalDocumentService';
import { FileText, Download, Loader } from 'lucide-react';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

function PortalDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await portalDocumentService.getSharedDocuments();
        setDocuments(response.data.data || []);
      } catch (err) {
        console.error('Failed to load shared documents:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const response = await portalDocumentService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Shared Documents</h1>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {documents.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p>No documents have been shared with you yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {doc.file_name}
                    </p>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                      {doc.file_size && (
                        <span className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</span>
                      )}
                      {doc.case_title && (
                        <span className="text-xs text-gray-500">
                          {doc.case_number ? `${doc.case_number} — ` : ''}{doc.case_title}
                        </span>
                      )}
                      {doc.shared_by && (
                        <span className="text-xs text-gray-500">
                          Shared by {doc.shared_by}
                        </span>
                      )}
                      {doc.shared_at && (
                        <span className="text-xs text-gray-400">
                          {new Date(doc.shared_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {doc.share_note && (
                      <p className="text-xs text-gray-600 mt-1 italic">
                        {doc.share_note}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleDownload(doc)}
                  disabled={downloading === doc.id}
                  className="ml-3 flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0"
                >
                  {downloading === doc.id ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalDocuments;
